import { type DragEndEvent, type DragStartEvent, PointerSensor, type PointerSensorOptions, useSensor, useSensors } from "@dnd-kit/core";
import { DateTime } from "luxon";
import React, { type MouseEvent, type SyntheticEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { noLocationString } from "../../../components/DayViewGaps/utils";
import { useCreateOrUpdateMultipleRecurringShifts } from "../../../components/RecurringShift/RecurringShiftMasterSchedule/hooks";
import { useDeleteRecurringShift, useUpdateRecurringShift } from "../../../components/RecurringShift/util/hooks";
import { RecurringShiftDisplayMode } from "../../../components/RecurringShift/util/useRecurringShiftState";
import type { ExactShiftFilterType } from "../../../components/staffing/MonthlyView/utils";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { SortingButtonHandle } from "../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { localStorageService, masterScheduleSortingKey } from "../../../services/LocalStorageService";
import { trackErrorEvent } from "../../../utils/analytics";
import { getWeekStart } from "../../../utils/dateUtils";
import {
	ChangesSource,
	type DepartmentLocation,
	DepartmentType,
	type Nurse,
	type NurseRole,
	type RecurringShift,
	type RecurringShiftWithApplyFromDate,
	type WeeklyRecurring,
} from "../../../utils/models";
import { compareValues, handleColumnSortClick, type SortingState } from "../../../utils/multiColumnSorting";
import { useLocationsCredentialsQueriesHook } from "../../Profile/v2/SynergyCredentials/credentialsHooks";
import { longPtoMinimumDays } from "../LongPTOButton/LongPTOButton";
import { usePersistedExclusionFilter, usePersistedShiftFilter } from "../usePersistedScheduleTemplateFilter";
import { CellMenuAction } from "./auxillary/RecurringMasterScheduleCellMenu";
import type { DraggableShiftData } from "./Cells/DraggableShift";
import type { DroppableCellOverData } from "./Cells/DroppableCell";
import { useFetchMasterSchedule } from "./hooks";
import { resolveEffectiveWeekStartDay } from "./resolveEffectiveWeekStartDay";
import { useGetMasterScheduleWeeksAndActiveState } from "./useGetMasterScheduleWeeksAndActiveState";
import {
	buildScheduleData,
	filterRecurringShifts,
	getDayIndex,
	masterScheduleDefaultSorting,
	masterScheduleSortingOptions,
	type Section,
	type SectionLocationMapReadOnly,
	toggleMasterScheduleNurseCellHighlight,
} from "./utils";

type ClickedCellWithAnchor = {
	anchorEl: HTMLElement;
	recurringShift: RecurringShift;
	clickedDate: DateTime;
	clickedLocation: DepartmentLocation;
};

type EditOrCreateRecurringShiftState = {
	mode: RecurringShiftDisplayMode;
	clickedDate: DateTime;
	recurringShift?: RecurringShift;
	clickedLocation: DepartmentLocation;
};

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 0.25 } } satisfies PointerSensorOptions;

const DEPARTMENT_TYPES = Object.values(DepartmentType);

export const useMasterScheduleV2State = () => {
	const { trackError } = useAnalytics();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [currentlyClickedCell, setCurrentlyClickedCell] = useState<ClickedCellWithAnchor | null>(null);
	const [editOrCreateRecurringShiftState, setEditOrCreateRecurringShiftState] = useState<EditOrCreateRecurringShiftState | null>(null);
	const { chosenDepartment } = useChosenDepartment();
	const { timezone, isMedicalDepartment } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const {
		roster,
		getNurseById,
		getNurseFormattedName,
		isLoading: isLoadingRoster,
		isError: isRosterError,
		refetch: refetchRoster,
	} = useRosterLookup(chosenDepartment.department_id);
	const enabledJobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const isSynergyMS = isMedicalDepartment;
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const amountOfWeeks = useMemo(() => chosenDepartment.master_schedule_weeks ?? 2, [chosenDepartment.master_schedule_weeks]);
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	// `viewStartDay` is the shared, localStorage-persisted week-start day (one source of truth across
	// Staffing, Profile, and Master Schedule). Master Schedule gates it behind its OWN flag: when the
	// flag is OFF the schedule template stays Sunday-anchored (0) regardless of the shared value, so the
	// biweekly phase math is byte-for-byte unchanged. `setViewStartDay` already persists.
	const { viewStartDay, setViewStartDay } = useWeekIndex();
	const effectiveWeekStartDay = resolveEffectiveWeekStartDay(isRuntimeWeekStartEnabled, viewStartDay);
	const firstDateOfCurrentWeek = useMemo(() => getWeekStart(DateTime.now().setZone(timezone), effectiveWeekStartDay), [timezone, effectiveWeekStartDay]);
	const weeksAndActiveState = useGetMasterScheduleWeeksAndActiveState({ firstDateToUse: firstDateOfCurrentWeek, weekStartDay: effectiveWeekStartDay });
	const [masterScheduleSortState, setMasterScheduleSortState] = useState<Array<SortingState<DepartmentLocation>>>(
		() => localStorageService.getTableSortingState<DepartmentLocation>(masterScheduleSortingKey, userId) ?? masterScheduleDefaultSorting,
	);
	const sortingMenuRef = useRef<SortingButtonHandle>(null);
	const [sectionOrder, setSectionOrder] = useState<Section[]>(() => localStorageService.getSectionOrder(chosenDepartment.department_id, userId) ?? []);
	const { expandedGroups, setExpandedGroups, toggleGroupExpand } = useExpandedGroups();
	const lastInitializedDepartmentRef = useRef<string | null>(null);
	const [showAllLocationsSections, setShowAllLocationsSections] = useState<Set<string>>(() => {
		/** Which sections are showing all locations (the ones that aren't in this Set are only showing top 3 locations
		 * */
		const saved = localStorageService.getExpandedLocationSections(chosenDepartment.department_id, userId);
		return saved ? new Set(saved) : new Set();
	});

	const [isDraggingSection, setIsDraggingSection] = useState(false);

	const [selectedRoleFilters, setSelectedRoleFilters] = usePersistedExclusionFilter("weekly", "roles", chosenDepartment.roles);
	const onSelectedRoleFiltersChanged = useCallback(
		(selected: (NurseRole | "all")[]) => {
			setSelectedRoleFilters(selected.includes("all") ? chosenDepartment.roles : selected.filter((role): role is NurseRole => role !== "all"));
		},
		[chosenDepartment.roles, setSelectedRoleFilters],
	);

	const deferredSelectedRoleFilters = useDeferredValue(selectedRoleFilters);

	// Job filtering (when FF on)
	const allJobIds = useMemo(() => enabledJobs.map((j) => j.id), [enabledJobs]);
	const [selectedJobFilters, onSelectedJobFiltersChanged] = usePersistedExclusionFilter("weekly", "jobs", allJobIds);
	const deferredSelectedJobFilters = useDeferredValue(selectedJobFilters);

	// Department type filtering for Synergy MS
	const [selectedDepartmentTypeList, setSelectedDepartmentTypeList] = usePersistedExclusionFilter("weekly", "departmentTypes", DEPARTMENT_TYPES);
	// The chips and the grid speak "all" for every type — a location whose type is none of the three still shows then.
	const selectedDepartmentTypes = useMemo<(DepartmentType | "all")[]>(
		() => (selectedDepartmentTypeList.length === DEPARTMENT_TYPES.length ? ["all"] : selectedDepartmentTypeList),
		[selectedDepartmentTypeList],
	);
	const onSelectedDepartmentTypesChanged = useCallback(
		(selected: (DepartmentType | "all")[]) => {
			setSelectedDepartmentTypeList(selected.includes("all") ? DEPARTMENT_TYPES : selected.filter((type): type is DepartmentType => type !== "all"));
		},
		[setSelectedDepartmentTypeList],
	);
	const deferredSelectedDepartmentTypes = useDeferredValue(selectedDepartmentTypes);

	const filtersBoxRef = React.useRef<HTMLDivElement>(null);
	// Optimistic drag/drop overlay: shifts shown immediately but not yet confirmed by the server.
	// Each entry is tagged with the mutation that created it so it can be retired independently once
	// that mutation settles (rapid drags must not clear each other's pending shifts).
	const [optimisticShifts, setOptimisticShifts] = useState<{ mutationId: string; shift: RecurringShift }[]>([]);
	const mutationIdCounterRef = useRef(0);
	const {
		data: recurringShiftsData,
		isLoading: isLoadingRecurringShifts,
		isError: isRecurringShiftsError,
		refetch: refetchRecurringShifts,
	} = useFetchMasterSchedule({
		startDate: firstDateOfCurrentWeek,
	});

	const [selectedNurseToFilterBy, setSelectedNurseToFilterBy] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const handleNurseFilterChange = useCallback((_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
		// Highlighting is handled by the effect keyed on the rendered grid + selected nurse.
		setSelectedNurseToFilterBy(value);
	}, []);

	const recurringDataInWeeks = useMemo(
		() =>
			filterRecurringShifts({
				recurringShiftsData,
				recurringTypes: ["Weekly"],
				interval: true,
				masterScheduleWeeks: amountOfWeeks,
			}),
		[recurringShiftsData, amountOfWeeks],
	);

	const recurringDataInWeeksWithoutIntervalLimit = useMemo(
		() =>
			filterRecurringShifts({
				recurringShiftsData,
				recurringTypes: ["Weekly"],
				masterScheduleWeeks: amountOfWeeks,
			}),
		[recurringShiftsData, amountOfWeeks],
	);

	const availableLocations = useMemo(() => {
		const names = Array.from(
			// We also include "out of config" locations.
			new Set([...chosenDepartment.locations.map((loc) => loc.location ?? ""), ...(recurringDataInWeeks?.map((shift) => shift.location ?? "") ?? [])]),
		);
		return names.sort((a, b) => {
			return compareValues(a, b, customStringOrderMap);
		});
	}, [chosenDepartment.locations, recurringDataInWeeks, customStringOrderMap]);
	const [selectedLocationFilters, onSelectedLocationFiltersChanged] = usePersistedExclusionFilter("weekly", "locations", availableLocations);
	const deferredSelectedLocationFilters = useDeferredValue(selectedLocationFilters);

	const allShiftsTimes = useMemo<ExactShiftFilterType[]>(
		() =>
			recurringDataInWeeks?.map((value) => ({
				start: DateTime.fromFormat(value.shiftStart.substring(0, 5), "HH:mm").setZone(timezone),
				end: DateTime.fromFormat(value.shiftEnd.substring(0, 5), "HH:mm").setZone(timezone),
			})) ?? // DEFAULT: shifts still loading — no time chips on offer yet, only All.
			[],
		[recurringDataInWeeks, timezone],
	);
	const { selectedShiftFilter, handleShiftFilterChange, offeredShiftWindows } = usePersistedShiftFilter("weekly", allShiftsTimes);

	// Fetch credentials for all unique locations (only in Synergy mode)
	const uniqueLocationIds = useMemo(() => {
		if (!isSynergyMS) return [];
		return Array.from(new Set(chosenDepartment.locations.map((loc) => loc.id).filter(Boolean)));
	}, [isSynergyMS, chosenDepartment.locations]);
	const { locationIdsAndCredentials } = useLocationsCredentialsQueriesHook({
		locationIds: uniqueLocationIds,
		onlyShowApproved: true,
	});

	const deferredSelectedShiftFilter = useDeferredValue(selectedShiftFilter);
	const deferredSelectedNurseToFilterBy = useDeferredValue(selectedNurseToFilterBy);

	// `calculatedData` is derived PURELY from server shifts + filters + the optimistic overlay
	const built = useMemo(
		() =>
			recurringShiftsData === undefined || roster === undefined
				? null
				: buildScheduleData({
						shifts: [
							...(recurringDataInWeeks ?? []).map((shift) => ({ shift, isOptimistic: false as const })),
							...optimisticShifts.map(({ shift }) => ({ shift, isOptimistic: true as const })),
						],
						chosenDepartment,
						weeksAndActiveState,
						amountOfWeeks,
						weekStartDay: effectiveWeekStartDay,
						timezone,
						isSynergyMS,
						jobManagementEnabled,
						selectedRoleFilters: deferredSelectedRoleFilters,
						selectedDepartmentTypes: deferredSelectedDepartmentTypes,
						selectedJobFilters: deferredSelectedJobFilters,
						selectedLocationFilters: deferredSelectedLocationFilters,
						selectedShiftFilter: deferredSelectedShiftFilter,
						nurseFilterId: deferredSelectedNurseToFilterBy?.value.id ?? null,
						masterScheduleSortState,
						sectionOrder,
						customStringOrderMap,
						locationIdsAndCredentials,
						getNurseById,
						getNurseFormattedName,
						longPtoMinimumDays,
						noLocationString,
					}),
		[
			recurringShiftsData,
			roster,
			recurringDataInWeeks,
			optimisticShifts,
			chosenDepartment,
			weeksAndActiveState,
			amountOfWeeks,
			effectiveWeekStartDay,
			timezone,
			isSynergyMS,
			jobManagementEnabled,
			deferredSelectedRoleFilters,
			deferredSelectedDepartmentTypes,
			deferredSelectedJobFilters,
			deferredSelectedLocationFilters,
			deferredSelectedShiftFilter,
			deferredSelectedNurseToFilterBy,
			masterScheduleSortState,
			sectionOrder,
			customStringOrderMap,
			locationIdsAndCredentials,
			getNurseById,
			getNurseFormattedName,
		],
	);
	const calculatedData: SectionLocationMapReadOnly | null = built?.data ?? null;
	const isCalculating = isLoadingRecurringShifts || isLoadingRoster;
	const loadError = isRecurringShiftsError || isRosterError;
	// The grid's error state offers one retry for whichever of its two loads failed.
	const retryLoad = useCallback(() => {
		if (isRosterError) refetchRoster();
		if (isRecurringShiftsError) void refetchRecurringShifts();
	}, [isRosterError, refetchRoster, isRecurringShiftsError, refetchRecurringShifts]);

	// `useDeferredValue` returns the previous reference while a recompute is pending, so a filter
	// value differing from its deferred counterpart means the visible grid is stale (the new filtered
	// result hasn't been computed/committed yet). Consumers fade the grid during this window.
	const isStale =
		!isCalculating &&
		(selectedRoleFilters !== deferredSelectedRoleFilters ||
			selectedJobFilters !== deferredSelectedJobFilters ||
			selectedDepartmentTypes !== deferredSelectedDepartmentTypes ||
			selectedLocationFilters !== deferredSelectedLocationFilters ||
			selectedShiftFilter !== deferredSelectedShiftFilter ||
			selectedNurseToFilterBy !== deferredSelectedNurseToFilterBy);

	// Surface shifts that couldn't be placed into any section (mirrors the previous analytics call).
	useEffect(() => {
		if (!built) return;
		for (const placementError of built.placementErrors) {
			trackError("MasterScheduleV2_optimisticallyAddShift_locationSectionDataNotFound", placementError);
		}
	}, [built, trackError]);

	// Highlight the filtered nurse's cells. Driven by what is actually rendered (`calculatedData`,
	// expanded sections, "show all") and applied on the next frame after commit.
	useEffect(() => {
		if (!selectedNurseToFilterBy) return;
		const nurseId = selectedNurseToFilterBy.value.id;
		const rafId = requestAnimationFrame(() => toggleMasterScheduleNurseCellHighlight(nurseId, true));
		return () => {
			cancelAnimationFrame(rafId);
			toggleMasterScheduleNurseCellHighlight(nurseId, false);
		};
	}, [selectedNurseToFilterBy, calculatedData, expandedGroups, showAllLocationsSections]);

	useEffect(() => {
		// Initialize expanded groups from localStorage on first data load (or department switch)
		if (!calculatedData) return;
		if (lastInitializedDepartmentRef.current === chosenDepartment.department_id) return;

		lastInitializedDepartmentRef.current = chosenDepartment.department_id;

		const allSections = Object.keys(calculatedData) as Section[];
		const savedCollapsed = localStorageService.getCollapsedSections(chosenDepartment.department_id, userId);

		if (savedCollapsed) {
			const collapsedSet = new Set(savedCollapsed);
			setExpandedGroups(new Set(allSections.filter((s) => !collapsedSet.has(s))));
		} else {
			setExpandedGroups(new Set(allSections));
		}

		// Also re-initialize showAllLocationsSections for the new department
		const savedExpandedLocSections = localStorageService.getExpandedLocationSections(chosenDepartment.department_id, userId);
		setShowAllLocationsSections(savedExpandedLocSections ? new Set(savedExpandedLocSections) : new Set());
	}, [calculatedData, chosenDepartment.department_id, userId, setExpandedGroups]);

	const onShiftClick = useCallback(
		(recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) =>
			setCurrentlyClickedCell({
				anchorEl: event.currentTarget as HTMLElement,
				recurringShift,
				clickedDate,
				clickedLocation,
			}),
		[],
	);
	const onShiftKeyDown = useCallback(
		(recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) =>
			(event.key === "Enter" || event.key === " ") &&
			setCurrentlyClickedCell({
				anchorEl: event.currentTarget as HTMLElement,
				recurringShift,
				clickedDate,
				clickedLocation,
			}),
		[],
	);

	const { isPending: isPartialDeleteLoading, mutateAsync: performUpdateRecurringShiftForPartialDeletion } = useUpdateRecurringShift();
	const { mutateAsync: performFullDeleteRecurringShift, isPending: isFullDeleteLoading } = useDeleteRecurringShift();

	const isDeleteLoading = useMemo(() => isPartialDeleteLoading || isFullDeleteLoading, [isPartialDeleteLoading, isFullDeleteLoading]);

	const handlePartialDeleteRecurringShift = useCallback(
		async ({ recurringShift, clickedDate, dateToUpdateFrom }: { recurringShift: RecurringShift; clickedDate: DateTime; dateToUpdateFrom: DateTime }) => {
			if (recurringShift?.id && clickedDate && dateToUpdateFrom) {
				if (recurringShift.recurringParams.recurringType === "Weekly") {
					const updatedRecurringShift: RecurringShift = {
						...recurringShift,
						recurringParams: {
							...recurringShift.recurringParams,
							weekdays: recurringShift.recurringParams.weekdays.filter((day) => getDayIndex(day) !== clickedDate.weekday % 7),
						},
					};
					if ((updatedRecurringShift.recurringParams as WeeklyRecurring).weekdays.length === 0) {
						//need to delete the recurring shift
						await performFullDeleteRecurringShift({
							id: recurringShift.id,
							source: ChangesSource.MasterSchedule,
							start_date: dateToUpdateFrom.toFormat("yyyy-MM-dd"),
							department_id: chosenDepartment.department_id,
							nurse_id: recurringShift.nurseId,
						});
					} else {
						//should only update the recurring shift
						await performUpdateRecurringShiftForPartialDeletion({
							recurringShift: updatedRecurringShift,
							source: ChangesSource.MasterSchedule,
							startDate: dateToUpdateFrom.toFormat("yyyy-MM-dd"),
						});
					}
					await refetchRecurringShifts();
					setCurrentlyClickedCell(null);
				} else {
					trackErrorEvent("MasterScheduleV2_partialDeleteNotSupportedForNonWeekly", {
						recurringShiftId: recurringShift.id,
						recurringType: recurringShift.recurringParams.recurringType,
					});
				}
			}
		},
		[performUpdateRecurringShiftForPartialDeletion, performFullDeleteRecurringShift, chosenDepartment, refetchRecurringShifts],
	);

	const onShiftMenuAction = useCallback(
		async (action: CellMenuAction, onDeleteDate?: DateTime | null) => {
			if (action === CellMenuAction.Cancel) {
				setCurrentlyClickedCell(null);
			} else if (action === CellMenuAction.GoToProfile) {
				if (currentlyClickedCell?.recurringShift) {
					const nurseId = currentlyClickedCell.recurringShift.nurseId;
					navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`, {
						state: { navigationSrc: window.location.pathname },
					});
					setCurrentlyClickedCell(null);
				}
			} else if (action === CellMenuAction.Edit) {
				if (currentlyClickedCell?.recurringShift) {
					setEditOrCreateRecurringShiftState({
						mode: RecurringShiftDisplayMode.Edit,
						clickedDate: currentlyClickedCell.clickedDate,
						recurringShift: currentlyClickedCell.recurringShift,
						clickedLocation: currentlyClickedCell.clickedLocation,
					});
					setCurrentlyClickedCell(null);
				}
			} else if (action === CellMenuAction.Delete) {
				const selectedCellDate = currentlyClickedCell?.clickedDate;
				if (onDeleteDate && selectedCellDate) {
					await handlePartialDeleteRecurringShift({
						recurringShift: currentlyClickedCell.recurringShift,
						clickedDate: selectedCellDate,
						dateToUpdateFrom: onDeleteDate,
					});
				} else {
					trackErrorEvent("MasterScheduleV2_noDateForDeleteAction", {
						action,
						onDeleteDate,
						selectedCellDate: selectedCellDate?.toISO(),
					});
				}
			}
		},
		[currentlyClickedCell, handlePartialDeleteRecurringShift, navigate, chosenDepartment.department_id],
	);

	const handleOnAddClick = useCallback((clickedDate: DateTime, clickedLocation: DepartmentLocation) => {
		setEditOrCreateRecurringShiftState({
			mode: RecurringShiftDisplayMode.Create,
			clickedDate: clickedDate,
			clickedLocation: clickedLocation,
		});
	}, []);

	const handleRecurringShiftComponentOnClose = useCallback(
		async (didUpdateOrCreate?: boolean) => {
			setEditOrCreateRecurringShiftState(null);
			if (didUpdateOrCreate) {
				await refetchRecurringShifts();
			}
		},
		[refetchRecurringShifts],
	);

	const persistExpandedGroups = useCallback(
		(newExpandedGroups: Set<string>) => {
			if (!calculatedData) return;
			const allSections = Object.keys(calculatedData) as Section[];
			const collapsed = allSections.filter((s) => !newExpandedGroups.has(s));
			localStorageService.setCollapsedSections(chosenDepartment.department_id, collapsed, userId);
		},
		[calculatedData, chosenDepartment.department_id, userId],
	);

	const persistShowAllLocationsSections = useCallback(
		(newShowAllSections: Set<string>) => {
			localStorageService.setExpandedLocationSections(chosenDepartment.department_id, Array.from(newShowAllSections), userId);
		},
		[chosenDepartment.department_id, userId],
	);

	const handleSectionCollapseToggle = useCallback(
		(section: Section) => {
			toggleGroupExpand(section);

			const wasExpanded = expandedGroups.has(section);
			const newExpanded = new Set(expandedGroups);
			if (wasExpanded) {
				newExpanded.delete(section);
			} else {
				newExpanded.add(section);
			}
			persistExpandedGroups(newExpanded);
		},
		[toggleGroupExpand, expandedGroups, persistExpandedGroups],
	);

	const toggleCollapseAll = useCallback(() => {
		if (!calculatedData) return;

		const allSections = Object.keys(calculatedData) as Section[];
		const allCollapsed = allSections.length > 0 && !expandedGroups.size;

		if (allCollapsed && previousCollapsedSectionsRef.current !== null) {
			// Restore previous collapsed state
			const restored = previousCollapsedSectionsRef.current;
			setExpandedGroups(restored);
			previousCollapsedSectionsRef.current = null;
			persistExpandedGroups(restored);
		} else if (allCollapsed) {
			// No saved state, expand all
			const allExpanded = new Set(allSections);
			setExpandedGroups(allExpanded);
			persistExpandedGroups(allExpanded);
		} else {
			// Save current state and collapse all
			previousCollapsedSectionsRef.current = expandedGroups;
			setExpandedGroups(new Set());
			persistExpandedGroups(new Set());
			// Reset "show all" locations state so all sections default to top 3
			const emptyShowAll = new Set<string>();
			setShowAllLocationsSections(emptyShowAll);
			persistShowAllLocationsSections(emptyShowAll);
		}
	}, [calculatedData, expandedGroups, setExpandedGroups, persistExpandedGroups, persistShowAllLocationsSections]);

	const toggleShowAllLocations = useCallback(
		(section: Section) => {
			setShowAllLocationsSections((prev) => {
				const next = new Set(prev);
				if (next.has(section)) {
					next.delete(section);
				} else {
					next.add(section);
				}
				persistShowAllLocationsSections(next);
				return next;
			});
		},
		[persistShowAllLocationsSections],
	);

	const handleSortHeaderClicked = useCallback(
		(e: MouseEvent, optionIndex: number) => {
			if (optionIndex < 0 || optionIndex >= masterScheduleSortingOptions.length) {
				trackError("MasterScheduleV2_handleSortHeaderClicked_invalidOptionIndex", {
					optionIndex,
					validRange: `0 - ${masterScheduleSortingOptions.length - 1}`,
				});
				return;
			}
			e.stopPropagation();
			sortingMenuRef.current?.openMenu();
			handleColumnSortClick({
				label: masterScheduleSortingOptions[optionIndex].label,
				columns: masterScheduleSortingOptions[optionIndex].sortColumns,
				sortState: masterScheduleSortState,
				setSortState: setMasterScheduleSortState,
				localStorageDetails: {
					localStorageTableKey: masterScheduleSortingKey,
					userId,
				},
				allowRemovalWhenDescendingToggle: false,
				appendToStartIfAlreadyExists: true,
			});
		},
		[masterScheduleSortState, trackError, userId],
	);

	const uniqueSections = useMemo<Section[]>(() => {
		if (!calculatedData) {
			return [];
		}
		return Array.from(new Set(Object.keys(calculatedData) as Section[]));
	}, [calculatedData]);

	const [activeDragId, setActiveDragId] = useState<string | null>(null);
	const [activeDragData, setActiveDragData] = useState<DraggableShiftData | null>(null);
	const previousCollapsedSectionsRef = useRef<Set<string> | null>(null);
	const ctrlClickedRef = useRef<boolean>(false);
	const { mutateAsync: handleCRUDRecurringShift } = useCreateOrUpdateMultipleRecurringShifts();

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			const dragData = event.active.data.current;
			if (dragData?.type === "section") {
				setIsDraggingSection(true);

				if (calculatedData) {
					const allSections = Object.keys(calculatedData) as Section[];
					const allAlreadyCollapsed = allSections.length > 0 && !expandedGroups.size;
					previousCollapsedSectionsRef.current = expandedGroups;

					if (!allAlreadyCollapsed) {
						setExpandedGroups(new Set());
					}
				}
			} else {
				const dragShiftData = event.active.data.current as unknown as DraggableShiftData;
				setActiveDragId(event.active.id?.toString() ?? null);
				setActiveDragData(dragShiftData);
			}
		},
		[expandedGroups, setExpandedGroups, calculatedData],
	);

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;
			setActiveDragId(null);
			setActiveDragData(null);
			setIsDraggingSection(false);

			const dragData = active?.data.current;
			if (dragData?.type === "section") {
				// Handle section drag
				if (over) {
					const dropData = over.data.current;
					if (dropData?.type === "section-drop") {
						const draggedSection = dragData.sectionName;
						const targetIndex = dropData.targetIndex;

						setSectionOrder((prevOrder) => {
							// Get current sections from calculatedData
							const currentSections = calculatedData ? Object.keys(calculatedData) : [];

							// If prevOrder is empty, initialize with current sections sorted
							const workingOrder = prevOrder.length > 0 ? [...prevOrder] : [...currentSections].sort();

							// Find current index of dragged section
							const currentIndex = workingOrder.indexOf(draggedSection);

							if (currentIndex === -1) {
								// Section not in order yet, add it at target index
								workingOrder.splice(targetIndex, 0, draggedSection);
							} else {
								// Remove from current position
								workingOrder.splice(currentIndex, 1);
								// Insert at new position (adjust if moving forward)
								const newIndex = currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
								workingOrder.splice(newIndex, 0, draggedSection);
							}
							if (workingOrder.length > 0) {
								localStorageService.setSectionOrder(chosenDepartment.department_id, workingOrder, userId);
							}
							return workingOrder;
						});
					}
				}
				// Restore previous collapsed state only if we saved one (whether valid drop or cancelled)
				if (previousCollapsedSectionsRef.current !== null) {
					setExpandedGroups(previousCollapsedSectionsRef.current);
				}
				return;
			}

			if (active && over) {
				// Handle shift drag
				const sourceData = active.data.current as unknown as DraggableShiftData;
				const dropData = over.data.current as unknown as DroppableCellOverData;
				if (dropData.cellDepartmentLocation.amount > 0 && recurringDataInWeeks) {
					const recurringShiftOfSource = recurringDataInWeeks.find((shift) => shift.id === sourceData.recurringShiftId);
					if (!recurringShiftOfSource) {
						showErrorSnackbar("Failed to find recurring shift data for the dragged shift.", {});
						return;
					}
					if (recurringShiftOfSource.recurringParams.recurringType !== "Weekly" || recurringShiftOfSource.recurringParams.interval === 1) {
						showSnackbar("We currently do not support drag&drop operations for shifts with weekly recurrence", "warning");
						return;
					}
					if (
						dropData.cellDay.dayDate === sourceData.day.dayDate &&
						recurringShiftOfSource.location === dropData.cellDepartmentLocation.location &&
						recurringShiftOfSource.shiftStart.substring(0, 5) === dropData.cellDepartmentLocation.shift_start.substring(0, 5) &&
						recurringShiftOfSource.shiftEnd.substring(0, 5) === dropData.cellDepartmentLocation.shift_end.substring(0, 5)
					) {
						// dropped in the same cell, do nothing
						showSnackbar("No changes applied", "info");
						return;
					}
					mutationIdCounterRef.current += 1;
					const mutationId = String(mutationIdCounterRef.current);
					const recurringShiftCopy: RecurringShift = {
						...recurringShiftOfSource,
						id: undefined, // we want to create a new one
						startDate: dropData.cellDay.dayDate.toFormat("yyyy-MM-dd"),
						shiftStart: dropData.cellDepartmentLocation.shift_start,
						shiftEnd: dropData.cellDepartmentLocation.shift_end,
						recurringParams: {
							...recurringShiftOfSource.recurringParams,
							recurringType: "Weekly",
							weekdays: [dropData.cellDay.dayDate.toFormat("cccc").toLowerCase()],
						},
						location: dropData.cellDepartmentLocation.location,
					};
					const processingDraggedNurseName = getNurseFormattedName(sourceData.nurseId);
					const isCopyOperation = ctrlClickedRef.current;
					const processingNamedKey = isCopyOperation ? "master.schedule.shift.copy.processing.named" : "master.schedule.shift.move.processing.named";
					const processingGenericKey = isCopyOperation ? "master.schedule.shift.copy.processing" : "master.schedule.shift.move.processing";
					showSnackbar(processingDraggedNurseName ? t(processingNamedKey, { nurseName: processingDraggedNurseName }) : t(processingGenericKey), "info");
					// Show the moved/copied shift immediately via the optimistic overlay; the build memo
					// folds it into the grid and the saved server shift supersedes it after the refetch.
					setOptimisticShifts((prev) => [...prev, { mutationId, shift: recurringShiftCopy }]);
					/**
					 * we want to move, and we only had 1 day before so we delete the previous shift
					 */
					const shouldDeletePreviousShift = !ctrlClickedRef.current && recurringShiftOfSource.recurringParams.weekdays.length === 1;

					/**
					 * In case of move but with multiple weekdays, we need to edit the previous shift to remove the day that was moved
					 */
					const shiftToEdit: RecurringShiftWithApplyFromDate | null =
						!shouldDeletePreviousShift && !ctrlClickedRef.current
							? {
									recurringShift: {
										...recurringShiftOfSource,
										recurringParams: {
											...recurringShiftOfSource.recurringParams,
											weekdays: recurringShiftOfSource.recurringParams.weekdays.filter((day) => day !== sourceData.day.dayDate.toFormat("cccc").toLowerCase()),
										},
									},
									applyFromDate: DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd"),
								}
							: null;

					try {
						await handleCRUDRecurringShift({
							departmentId: chosenDepartment.department_id,
							newRecurringShifts: [recurringShiftCopy],
							shiftsToRemove: shouldDeletePreviousShift
								? [
										{
											recurringShift: recurringShiftOfSource,
											applyFromDate: recurringShiftOfSource.startDate,
										},
									]
								: [],
							existingRecurringShifts: !shouldDeletePreviousShift && shiftToEdit ? [shiftToEdit] : [],
							source: ChangesSource.MasterSchedule,
							nurseId: sourceData.nurseId,
						});
						await refetchRecurringShifts();
					} catch {
						// The mutation hook already shows a generic error; surface the "reverted" message too.
						// Dropping the overlay entry in `finally` removes the optimistic shift (i.e. the revert).
						const nurseName = getNurseFormattedName(sourceData.nurseId);
						showSnackbar(
							nurseName ? t("master.schedule.shift.move.error.reverted.named", { nurseName }) : t("master.schedule.shift.move.error.reverted"),
							"info",
						);
					} finally {
						// Retire only this drag's overlay entry so concurrent drags keep their pending shifts.
						setOptimisticShifts((prev) => prev.filter((entry) => entry.mutationId !== mutationId));
					}
				} else {
					showErrorSnackbar("Cannot drop shift here as there are no available slots in this location.", {});
				}
			}
		},
		[
			recurringDataInWeeks,
			handleCRUDRecurringShift,
			chosenDepartment,
			showSnackbar,
			refetchRecurringShifts,
			timezone,
			showErrorSnackbar,
			calculatedData,
			userId,
			setExpandedGroups,
			getNurseFormattedName,
			t,
		],
	);
	const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));

	const onCtrlPressedChange = useCallback((isCtrlPressed: boolean) => {
		/**
		 * TODO - we have a bug with the ctrl key detection where it sometimes stays stuck in true state
		 * so for now we will disable this functionality until we can find a better solution
		 *
		 * This results in sometimes performing copy instead of move when dragging a shift, even though ctrl is not pressed!
		 *
		 * NOTE: this also is disabled in frontend/src/pages/MasterSchedule/v2/Cells/DraggableShift.tsx
		 */
		// ctrlClickedRef.current = isCtrlPressed;
		return;
	}, []);

	const allSections = calculatedData ? Object.keys(calculatedData) : [];
	const allSectionsCollapsed = allSections.length > 0 && !expandedGroups.size;

	return {
		weeksAndActiveState,
		firstDateOfCurrentWeek,
		selectedShiftFilter,
		handleShiftFilterChange,
		allShiftsTimes,
		offeredShiftWindows,
		filtersBoxRef,
		calculatedData,
		isCalculating,
		loadError,
		retryLoad,
		isStale,
		onShiftClick,
		onShiftKeyDown,
		currentlyClickedCell,
		editOrCreateRecurringShiftState,
		onShiftMenuAction,
		isDeleteLoading,
		handleOnAddClick,
		amountOfWeeks,
		selectedRoleFilters,
		chosenDepartment,
		handleRecurringShiftComponentOnClose,
		onSelectedRoleFiltersChanged,
		departmentSpecificRoles: chosenDepartment.roles,
		departmentJobs: enabledJobs,
		selectedJobFilters,
		onSelectedJobFiltersChanged,
		isJobFilterActive: selectedJobFilters.length < allJobIds.length,
		jobManagementEnabled,
		selectedDepartmentTypes,
		onSelectedDepartmentTypesChanged,
		recurringDataInWeeks,
		refetchRecurringShifts,
		getNurseById,
		getNurseFormattedName,
		handleNurseFilterChange,
		selectedNurseToFilterBy,
		masterScheduleSortState,
		setMasterScheduleSortState,
		handleSectionCollapseToggle,
		toggleCollapseAll,
		expandedGroups,
		timezone,
		handleSortHeaderClicked,
		sortingMenuRef,
		uniqueSections,
		activeDragId,
		activeDragData,
		handleDragStart,
		handleDragEnd,
		sensors,
		onCtrlPressedChange,
		recurringDataInWeeksWithoutIntervalLimit,
		isSynergyMS,
		sectionOrder,
		isDraggingSection,
		allSectionsCollapsed,
		showAllLocationsSections,
		toggleShowAllLocations,
		availableLocations,
		selectedLocationFilters,
		onSelectedLocationFiltersChanged,
		weekStartDay: effectiveWeekStartDay,
		setWeekStartDay: setViewStartDay,
	};
};
